import { ErrorBoundary } from "react-error-boundary";
import ErrorPage from "./components/error-page";
import type { FC } from "react";

import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import LaunchPage from "./pages/launch-page";
import AuthRedirectPage from "./pages/auth-redirect-page";
import FHIRContextProvider from "./contexts/FHIRContext/Provider";
import PatientEmbedPage from "./pages/patient-embed-page";
import PatientListPage from "./pages/patient-list-page";
import MalnutritionPage from "./pages/malnutrition-page";
import MalnutritionListPage from "./pages/malnutrition-list-page";
import { QueryClient, QueryClientProvider } from "react-query";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
      staleTime: Infinity,
    },
  },
});

// FHIR App Route wrapper
const FHIRAppRoute: FC = () => {
  return (
    <FHIRContextProvider>
      <AuthRedirectPage />
    </FHIRContextProvider>
  );
};

const EMR_MODE_ROUTES: Record<string, string> = {
  malnutrition: "/malnutrition",
  malnutrition_list: "/malnutrition/list",
};

// The EMR launches every app at "/" with an emr_mode flag. emr_mode=malnutrition
// routes to the malnutrition app and emr_mode=malnutrition_list to its list;
// anything else renders the standard patient embed. The rest of the launch
// query string (emr_patient_id, emr_hospital_id, …) is preserved so the target
// page keeps its context, but emr_mode itself is dropped — it's consumed by
// this redirect and shouldn't linger on the route.
const RootRoute: FC = () => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const emrMode = params.get("emr_mode")?.toLowerCase();
  const pathname = Object.hasOwn(EMR_MODE_ROUTES, emrMode)
    ? EMR_MODE_ROUTES[emrMode]
    : undefined;
  if (pathname) {
    params.delete("emr_mode");
    const nextSearch = params.toString();
    return (
      <Navigate
        to={{ pathname, search: nextSearch ? `?${nextSearch}` : "" }}
        replace
      />
    );
  }
  return <PatientEmbedPage />;
};

const App: FC = () => {
  const basename = process.env.REACT_APP_BASE_URL || "/patient-embed";

  return (
    <ErrorBoundary FallbackComponent={ErrorPage}>
      <Router basename={basename}>
        <QueryClientProvider client={queryClient}>
          <Routes>
            <Route path="/" element={<RootRoute />} />
            <Route path="/list" element={<PatientListPage />} />
            <Route path="/malnutrition" element={<MalnutritionPage />} />
            <Route
              path="/malnutrition/list"
              element={<MalnutritionListPage />}
            />
            {/* Future SMART on FHIR routes */}
            <Route path="/launch" element={<LaunchPage />} />
            <Route path="/app" element={<FHIRAppRoute />} />
          </Routes>
        </QueryClientProvider>
      </Router>
    </ErrorBoundary>
  );
};
export default App;
